const createVirtualStage = async (drawing) => {
  const is_vertical = drawing.image?.label.includes("vertical");
  const stageSize = is_vertical ? { width: 300, height: 400 } : { width: 400, height: 300 };

  const stage = new Konva.Stage({
    container: document.createElement('div'),
    width: stageSize.width,
    height: stageSize.height,
  });

  const layer = new Konva.Layer();
  stage.add(layer);

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
  };

  try {
    const image = drawing.image?.image;
    const lines = drawing.lines;
    const stamps = drawing.stamps;
    
    if (image) {              
      const img = await loadImage(image);
      const konvaImage = new Konva.Image({
        image: img,
        width: stage.width(),
        height: stage.height()
      });
      layer.add(konvaImage);
    }

    if (lines) {
      lines.forEach((line) => {
        if (line.tool === "pen") {
          const konvaLine = new Konva.Line({
            points: line.points.flatMap(p => [p.x * stage.width(), p.y * stage.height()]),
            stroke: line.color,
            strokeWidth: line.strokeSize,
            tension: 0.5,
            lineCap: 'round'
          });
          layer.add(konvaLine);
        }
        else if (line.tool.includes("square")) {
          const getSubdivision = () => {
            switch (line.tool) {
              case "square":
                return { x: 1, y: 1 };
              case "square2":
                return { x: 2, y: 1 };
              case "square3":
                return { x: 3, y: 1 };
              case "square4":
                return { x: 4, y: 1 };
              case "square_matrix":
                return { x: 2, y: 2 };
              default:
                return { x: 1, y: 1 };
            }
          }

          const subdivision = getSubdivision();
          const squareWidth = line.info[0].width * stage.width() / subdivision.x;
          const squareHeight = line.info[0].height * stage.height() / subdivision.y;
          const startX = line.info[0].startX * stage.width();
          const startY = line.info[0].startY * stage.height();
            
          for (let i = 0; i < subdivision.x; i++) {
            for (let j = 0; j < subdivision.y; j++) {
              const square = new Konva.Rect({
                x: startX + i * squareWidth,
                y: startY + j * squareHeight,
                width: squareWidth,
                height: squareHeight,
                stroke: line.color,
                strokeWidth: line.strokeSize
              });
              layer.add(square);
            }
          }
        }
        else if (line.tool === "arrow") {
          if (line.isDoubleArrow) {
            const startX = line.info[0].startX * stage.width();
            const startY = line.info[0].startY * stage.height();

            const arrowVector = {
              x: line.info[0].width * stage.width(),
              y: line.info[0].height * stage.height()
            };

            const inverseArrowVector = {
              x: -arrowVector.x * line.strokeSize * 2 / Math.sqrt(arrowVector.x * arrowVector.x + arrowVector.y * arrowVector.y),
              y: -arrowVector.y * line.strokeSize * 2 / Math.sqrt(arrowVector.x * arrowVector.x + arrowVector.y * arrowVector.y)
            };

            const thresholdVector = {
              x: -arrowVector.y * line.strokeSize / Math.sqrt(arrowVector.x * arrowVector.x + arrowVector.y * arrowVector.y),
              y: arrowVector.x * line.strokeSize / Math.sqrt(arrowVector.x * arrowVector.x + arrowVector.y * arrowVector.y)
            };

            const trianglePoints = [
              0,
              0,
              (inverseArrowVector.x + thresholdVector.x * 1.2) * 2,
              (inverseArrowVector.y + thresholdVector.y * 1.2) * 2,
              (inverseArrowVector.x - thresholdVector.x * 1.2) * 2,
              (inverseArrowVector.y - thresholdVector.y * 1.2) * 2
            ];

            const group = new Konva.Group({
              x: startX,
              y: startY,
              rotation: line.info[0].rotation || 0
            });

            const line1 = new Konva.Line({
              x: thresholdVector.x,
              y: thresholdVector.y, 
              points: [
                0,
                0,
                arrowVector.x + inverseArrowVector.x * 2,
                arrowVector.y + inverseArrowVector.y * 2
              ],
              stroke: line.color,
              strokeWidth: line.strokeSize,
              dash: line.isDashed ? [15, 10] : undefined
            });

            const line2 = new Konva.Line({
              x: -thresholdVector.x,
              y: -thresholdVector.y,
              points: [
                0,
                0,
                arrowVector.x + inverseArrowVector.x * 2,
                arrowVector.y + inverseArrowVector.y * 2
              ],
              stroke: line.color,
              strokeWidth: line.strokeSize,
              dash: line.isDashed ? [15, 10] : undefined
            });

            const triangle = new Konva.Line({
              x: arrowVector.x,
              y: arrowVector.y,
              points: trianglePoints,
              stroke: line.color,
              strokeWidth: line.strokeSize,
              fill: line.color,
              closed: true
            });

            group.add(line1);
            group.add(line2);
            group.add(triangle);
            layer.add(group);
          }
          else {  
            const arrow = new Konva.Arrow({
              points: [
                  line.info[0].startX * stage.width(),
                  line.info[0].startY * stage.height(),
                  (line.info[0].startX + line.info[0].width) * stage.width(),
                  (line.info[0].startY + line.info[0].height) * stage.height()
              ],
              stroke: line.color,
              strokeWidth: line.strokeSize,
              dash: line.isDashed ? [15, 10] : undefined
            });
              
            layer.add(arrow);
          }
        }
      })
    }

    if (stamps) {
      for (const stamp of stamps) {
        const img = await loadImage(stamp.src);
        const scaleX = stageSize.width / stamp.originalStageSize.width;
        const scaleY = stageSize.height / stamp.originalStageSize.height;
        
        const konvaStamp = new Konva.Image({
          image: img,
          scaleX: scaleX,
          scaleY: scaleY,
          x: stamp.x * scaleX,
          y: stamp.y * scaleY,
          width: stamp.width,
          height: stamp.height,
          rotation: stamp.rotation || 0,
        });

        layer.add(konvaStamp);
      }
    }

    layer.draw();
    return stage;
  } 
  catch (error) {
    console.error("Error creating virtual stage:", error);
    stage.destroy();
    return null;
  }
};

export default createVirtualStage;